import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import AppLayout from '@/components/layout/AppLayout';
import DashboardPage from '@/pages/DashboardPage';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import LocationsPage from '@/pages/LocationsPage';
import ItemsPage from '@/pages/ItemsPage';
import RequisitionsPage from '@/pages/RequisitionsPage';
import ApprovalsPage from '@/pages/ApprovalsPage';
import PurchaseOrdersPage from '@/pages/PurchaseOrdersPage';
import ReceiveGoodsPage from '@/pages/ReceiveGoodsPage';
import VendorsPage from '@/pages/VendorsPage';
import ReportsPage from '@/pages/ReportsPage';
import StockTransfersPage from '@/pages/StockTransfersPage';
import InventoryPage from '@/pages/InventoryPage';
import SettingsPage from '@/pages/SettingsPage';
import ProfilePage from '@/pages/ProfilePage';
import NotFoundPage from '@/pages/NotFoundPage';
import BudgetOverviewPage from '@/pages/BudgetOverviewPage';
import BudgetTransactionsPage from '@/pages/BudgetTransactionsPage';
import AutoReorderPage from '@/pages/AutoReorderPage';
import ExpiryDashboardPage from '@/pages/ExpiryDashboardPage';
import ReturnsPage from '@/pages/ReturnsPage';
import AuditLogPage from '@/pages/AuditLogPage';
import ContractsPage from '@/pages/ContractsPage';
import AdvancedAnalyticsPage from '@/pages/AdvancedAnalyticsPage';
import MobileInventoryPage from '@/pages/MobileInventoryPage';
import ConsumptionPage from '@/pages/ConsumptionPage';
import EquipmentPage from '@/pages/EquipmentPage';
import SerialNumbersPage from '@/pages/SerialNumbersPage';
import StockCountPage from '@/pages/StockCountPage';
import RecallsPage from '@/pages/RecallsPage';
import CurrenciesPage from '@/pages/CurrenciesPage';
import AssemblyPage from '@/pages/AssemblyPage';
import QualityControlPage from '@/pages/QualityControlPage';
import SecurityMFAPage from '@/pages/SecurityMFAPage';
import WebhooksPage from '@/pages/WebhooksPage';
import DashboardCustomizationPage from '@/pages/DashboardCustomizationPage';
import DataExportImportPage from '@/pages/DataExportImportPage';

// Protected route wrapper - wraps layout and renders nested routes
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

// Public route wrapper (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />

      {/* Protected routes with layout */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/locations" element={<LocationsPage />} />
        <Route path="/items" element={<ItemsPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/inventory" element={<SettingsPage />} />
        <Route path="/settings/purchase" element={<SettingsPage />} />
        <Route path="/settings/approvals" element={<SettingsPage />} />
        <Route path="/settings/general" element={<SettingsPage />} />
        <Route path="/requisitions" element={<RequisitionsPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="/receive-goods" element={<ReceiveGoodsPage />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/stock-transfers" element={<StockTransfersPage />} />
        <Route path="/budget" element={<BudgetOverviewPage />} />
        <Route path="/budget-transactions" element={<BudgetTransactionsPage />} />
        <Route path="/auto-reorder" element={<AutoReorderPage />} />
        <Route path="/expiry" element={<ExpiryDashboardPage />} />
        <Route path="/returns" element={<ReturnsPage />} />
        <Route path="/audit-log" element={<AuditLogPage />} />
        <Route path="/contracts" element={<ContractsPage />} />
        <Route path="/analytics" element={<AdvancedAnalyticsPage />} />
        <Route path="/mobile-inventory" element={<MobileInventoryPage />} />
        <Route path="/consumption" element={<ConsumptionPage />} />
        <Route path="/equipment" element={<EquipmentPage />} />
        <Route path="/serial-numbers" element={<SerialNumbersPage />} />
        <Route path="/stock-count" element={<StockCountPage />} />
        <Route path="/recalls" element={<RecallsPage />} />
        <Route path="/currencies" element={<CurrenciesPage />} />
        <Route path="/assembly" element={<AssemblyPage />} />
        <Route path="/quality-control" element={<QualityControlPage />} />
        <Route path="/security" element={<SecurityMFAPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        <Route path="/dashboard-customization" element={<DashboardCustomizationPage />} />
        <Route path="/data-export-import" element={<DataExportImportPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
